import React, { useEffect, useRef, useState } from 'react';
import { showMessage } from '../../actions';

function RedditVideoPreview({ videoData, originalUrl }) {
  const videoRef = useRef(null);
  const [isMp4Fallback, setIsMp4Fallback] = useState(false);
  const [isUserMuted, setIsUserMuted] = useState(true);
  const isMuted = isUserMuted;

  const toggleMuted = () => {
    setIsUserMuted(muted => !muted);
  };

  const handleVideoKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMuted();
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver !== 'function') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          const playback = video.play();
          if (playback && typeof playback.catch === 'function') {
            playback.catch(error => {
              if (error.name === 'AbortError') return;
              console.error('Unable to resume Reddit video playback', error);
              showMessage(`Unable to resume Reddit video playback: ${error}`);
            });
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const hasMediaSource =
      typeof window !== 'undefined' &&
      ('MediaSource' in window ||
        'ManagedMediaSource' in window ||
        'WebKitMediaSource' in window);
    const supportsDash = Boolean(videoData.dashUrl) && hasMediaSource;
    setIsMp4Fallback(!supportsDash);

    if (!video || !supportsDash) {
      return undefined;
    }

    let player;
    let cancelled = false;
    let usingFallback = false;

    const useMp4Fallback = error => {
      if (cancelled || usingFallback) return;
      usingFallback = true;
      setIsMp4Fallback(true);
      console.error('Unable to initialize Reddit DASH video player', error);
      const message = getDashErrorMessage(error);
      showMessage(`Reddit DASH playback error: ${message}`);
      if (player) {
        player.reset();
      }
      video.src = videoData.url;
      video.load();
    };

    import('dashjs')
      .then(dashjsModule => {
        if (cancelled) return;

        let dashjs = dashjsModule;
        const visitedModules = new Set();
        while (dashjs && !visitedModules.has(dashjs)) {
          visitedModules.add(dashjs);
          if (typeof dashjs.MediaPlayer === 'function') break;
          dashjs = dashjs.default || dashjs.dashjs;
        }
        if (!dashjs || typeof dashjs.MediaPlayer !== 'function') {
          throw new Error('dash.js MediaPlayer export is unavailable');
        }
        if (
          typeof dashjs.supportsMediaSource === 'function' &&
          !dashjs.supportsMediaSource()
        ) {
          setIsMp4Fallback(true);
          return;
        }

        player = dashjs.MediaPlayer().create();
        const handleError = error => {
          player.off(dashjs.MediaPlayer.events.ERROR, handleError);
          useMp4Fallback(error);
        };

        player.on(dashjs.MediaPlayer.events.ERROR, handleError);
        player.initialize(video, videoData.dashUrl, true);
      })
      .catch(useMp4Fallback);

    return () => {
      cancelled = true;
      if (player) {
        player.reset();
      }
    };
  }, [videoData.dashUrl, videoData.url]);

  return (
    <>
      <div className="PostDetail__video-preview-wrap">
        <video
          ref={videoRef}
          src={videoData.url}
          autoPlay
          playsInline
          muted={isMuted}
          loop
          role="button"
          tabIndex={0}
          aria-label={isMuted ? 'Muted video. Unmute' : 'Video unmuted. Mute'}
          onClick={toggleMuted}
          onKeyDown={handleVideoKeyDown}
          width={videoData.w}
          height={videoData.h}
          className="PostDetail__preview PostDetail__preview--video"
        />
        <span className="PostDetail__video-mute-indicator" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M3 9v6h4l5 4V5L7 9H3z" />
            {isMuted ? (
              <path d="m16 9 5 6m0-6-5 6" />
            ) : (
              <>
                <path d="M15.5 8.5a5 5 0 0 1 0 7" />
                <path d="M18 6a8.5 8.5 0 0 1 0 12" transform="translate(1 0)" />
              </>
            )}
          </svg>
        </span>
      </div>
      {isMp4Fallback && (
        <p className="PostDetail__preview-claimer UserContent">
          video preview -{' '}
          <a href={originalUrl} target="_blank" rel="noopener noreferrer">
            original
          </a>{' '}
          might have sound
        </p>
      )}
    </>
  );
}

function getDashErrorMessage(error) {
  if (!error || typeof error !== 'object') {
    return String(error);
  }

  if (error.error && typeof error.error === 'object' && error.error.message) {
    return error.error.message;
  }
  if (error.event && typeof error.event === 'object' && error.event.message) {
    return error.event.message;
  }
  if (typeof error.error === 'string') {
    return typeof error.event === 'string'
      ? `${error.error}: ${error.event}`
      : error.error;
  }
  if (typeof error.event === 'string') {
    return error.event;
  }
  return error.message || error.type || 'Unknown DASH error';
}

export default RedditVideoPreview;
